// ExportPDFButton.js
import React, { useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-simple-map-screenshoter';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Cropper from 'react-easy-crop';

// Fonction pour obtenir l'image recadrée
const getCroppedImage = (imageSrc, crop) =>
  new Promise((resolve) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = crop.width;
      canvas.height = crop.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        crop.width,
        crop.height
      );
      resolve(canvas.toDataURL('image/png'));
    };
  });

const ExportPDFButton = ({ titre, surfaceRestante }) => {
  const map = useMap();
  const [isExporting, setIsExporting] = useState(false);

  // Gestion PA
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({ 
    extraitTF: true,
    extraitPA: false 
  });
  const [paSrc, setPaSrc] = useState(null);
  const [croppedPA, setCroppedPA] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);

  
  const formatDate = (date) =>
    new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  const getTitreDisplayName = () =>
    String(titre.titref || titre.graphic_de || titre.num || 'Non défini');

  const handlePAUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPaSrc(reader.result);
      setShowCropper(true);
      // Réinitialiser l'aperçu précédent si on upload une nouvelle image
      setCroppedPA(null);
    };
    reader.readAsDataURL(file);
  };

  const handleValidateCrop = async () => {
    if (!cropPixels) return;
    const img = await getCroppedImage(paSrc, cropPixels);
    setCroppedPA(img);
    setShowCropper(false);
  };

  const handleEditCrop = () => {
    setShowCropper(true);
  };

  const handleRemovePA = () => {
    setPaSrc(null);
    setCroppedPA(null);
    setShowCropper(false);
    // Décocher automatiquement la case si on supprime l'image
    setConfig({ ...config, extraitPA: false });
  };

  const handleExportPDF = async () => {
    if (isExporting) return;
    setShowConfig(false);
    setIsExporting(true);

    try {

      let tfImage = null;
      if (config.extraitTF) {
        map.closePopup();
        const hideElements = [
          '.leaflet-control',
          '#measure-panel',
          '#import-Control',
          '.leaflet-draw',
          '.search-panel-modern',
          '.leaflet-popup',
          '.leaflet-popup-pane',
          '.leaflet-tooltip',
          '.leaflet-tooltip-pane'
        ];
        hideElements.forEach(selector => {
          document.querySelectorAll(selector).forEach(el => {
            el.setAttribute('data-original-display', el.style.display || '');
            el.style.display = 'none';
          });
        });

        const geometry = JSON.parse(titre.geom_geojson);
        const bounds = L.geoJSON(geometry).getBounds();
        if (bounds.isValid()) map.fitBounds(bounds, {
        padding: [20, 20],   
        maxZoom: 23,
        animate: false
      });

        await new Promise(r => setTimeout(r, 2000));

        if (!map.screenshoter) L.simpleMapScreenshoter({ hidden: true }).addTo(map);

        const blob = await map.screenshoter.takeScreen('image/png');
        tfImage = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        hideElements.forEach(selector => {
          document.querySelectorAll(selector).forEach(el => {
            const originalDisplay = el.getAttribute('data-original-display');
            el.style.display = originalDisplay !== 'none' ? originalDisplay : '';
            el.removeAttribute('data-original-display');
          });
        });
      }

      // 🎯 Création du PDF
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // EN-TÊTE
      pdf.setFillColor(40, 128, 185);
      pdf.rect(0, 0, pageWidth, 30, 'F');
      pdf.setFontSize(22);
      pdf.setTextColor(255);
      pdf.setFont('helvetica', 'bold');
      pdf.text('RAPPORT DU TITRE FONCIER', pageWidth / 2, 20, { align: 'center' });
      pdf.setFontSize(10);
      pdf.text(`Généré le ${formatDate(new Date())}`, pageWidth - margin, 25, { align: 'right' });

      yPos = 40;

      // SECTION 1: CARTE TITRE FONCIER
      if (config.extraitTF && tfImage) {
        pdf.setFontSize(14);
        pdf.setTextColor(40, 128, 185);
        pdf.setFont('helvetica', 'bold');
        pdf.text('1. LOCALISATION', margin, yPos);
        yPos += 5;
        pdf.setDrawColor(40, 128, 185);
        pdf.setLineWidth(0.3);
        pdf.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;

        const img = new Image();
        img.src = tfImage;
        await new Promise(resolve => {
          img.onload = () => {
            const imgWidth = pageWidth - 2 * margin;
            const imgHeight = (img.height * imgWidth) / img.width;
            const imageHeight = Math.min(imgHeight, pageHeight * 0.3);
            pdf.addImage(tfImage, 'PNG', margin, yPos, imgWidth, imageHeight);
            yPos += imageHeight + 5;
            pdf.setFontSize(8);
            pdf.setTextColor(100, 100, 100);
            pdf.text('Extrait cartographique du titre foncier', pageWidth / 2, yPos, { align: 'center' });
            yPos += 15;
            resolve();
          };
        });
      }

      if (config.extraitPA && croppedPA) {
        if (yPos > pageHeight - 90) {
          pdf.addPage();
          yPos = margin;
        }
        pdf.setFontSize(14);
        pdf.setTextColor(40, 128, 185);
        pdf.setFont('helvetica', 'bold');
        pdf.text("2. EXTRAIT DU PLAN D'AMÉNAGEMENT", margin, yPos);
        yPos += 5;
        pdf.setDrawColor(40, 128, 185);
        pdf.setLineWidth(0.3);
        pdf.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
        
        const paImg = new Image();
        paImg.src = croppedPA;
        await new Promise(resolve => {
          paImg.onload = () => {
            const imgWidth = pageWidth - 2 * margin;
            const imgHeight = (paImg.height * imgWidth) / paImg.width;
            const imageHeight = Math.min(imgHeight, pageHeight * 0.3);
            pdf.addImage(croppedPA, 'PNG', margin, yPos, imgWidth, imageHeight);
            yPos += imageHeight + 10;
            pdf.setFontSize(8);
            pdf.setTextColor(100, 100, 100);
            pdf.text("Extrait du PA", pageWidth / 2, yPos, { align: 'center' });
            yPos += 15;
            resolve();
          };
        });
      }

      // SECTION 3: DONNÉES SYNTHÉTIQUES DU TITRE
      if (yPos > pageHeight - 70) {
        pdf.addPage();
        yPos = margin;
      }

      pdf.setFontSize(14);
      pdf.setTextColor(40, 128, 185);
      pdf.setFont('helvetica', 'bold');
      pdf.text('3. Informations du titre', margin, yPos);
      yPos += 5;
      pdf.setDrawColor(40, 128, 185);
      pdf.setLineWidth(0.3);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      const infoData = [
        ['Numéro du titre foncier', getTitreDisplayName()],
        ['Zone administrative', titre.pa_nom || 'Non spécifié'],
        ['Surface brute', titre.surf_adop ? `${Number(titre.surf_adop).toFixed(2)} m²` : 'Non disponible'],
        ['Surface nette', `${Number(surfaceRestante).toFixed(2)} m²`]
      ];

      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');

      infoData.forEach(([label, value], index) => {
        if (index % 2 === 0) {
          pdf.setFillColor(245, 245, 245);
          pdf.rect(margin, yPos - 2, pageWidth - 2 * margin, 10, 'F');
        }
        pdf.text(`${label}`, margin + 5, yPos + 5);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${value}`, margin + 50, yPos + 5);
        pdf.setFont('helvetica', 'normal');
        yPos += 10;
      });

      yPos += 5;

      // SECTION 4: TABLEAU DES ZONAGES
      if (titre.zonages && titre.zonages.length > 0) {
        if (yPos > pageHeight - 80) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(14);
        pdf.setTextColor(40, 128, 185);
        pdf.setFont('helvetica', 'bold');
        pdf.text('4. ZONAGES ', margin, yPos);
        yPos += 5;
        pdf.setDrawColor(40, 128, 185);
        pdf.setLineWidth(0.3);
        pdf.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;

        const tableData = titre.zonages.map(z => [
          z.type_zonage || 'Non défini',
          `${Number(z.surface_intersection_totale_m2 || 0).toFixed(2)} m²`,
          `${Number(z.pourcentage_intersection || 0).toFixed(2)} %`
        ]);

        autoTable(pdf, {
          startY: yPos,
          head: [['Type de zonage', 'Surface', 'Pourcentage']],
          body: tableData,
          margin: { left: margin, right: margin },
          styles: { 
            fontSize: 8, 
            cellPadding: 3, 
            overflow: 'linebreak',
            textColor: [0, 0, 0]
          },
          headStyles: { 
            fillColor: [40, 128, 185], 
            textColor: [255, 255, 255], 
            fontStyle: 'bold',
            fontSize: 9
          },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 40 },
            2: { cellWidth: 35 }
          },
          didDrawPage: (data) => {
            yPos = data.cursor.y + 10;
          }
        });
      } else {
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont('helvetica', 'italic');
        pdf.text('Aucun zonage associé à ce titre.', margin, yPos);
        yPos += 10;
      }

      // PIED DE PAGE
      const pageCount = pdf.internal.getNumberOfPages();
      for(let i = 1; i <= pageCount; i++){
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} / ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text(`Réf: ${getTitreDisplayName().substring(0, 20)}...`, margin, pageHeight - 10);
        pdf.text(formatDate(new Date()), pageWidth - margin, pageHeight - 10, { align: 'right' });
      }

      pdf.save(`Rapport_Titre_${titre.gid}.pdf`);
      setIsExporting(false);

    } catch (error) {
      console.error(error);
      setIsExporting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowConfig(true)}
        style={buttonStyle}
        title="Générer un rapport PDF détaillé"
      >
        {isExporting ? '🔄 Génération en cours...' : 'Exporter le rapport'}
      </button>

      {showConfig && (
        <>
          {/* Overlay pour fermer le panneau en cliquant à côté */}
          <div style={overlayStyle} onClick={() => setShowConfig(false)} />
          
          {/* Panneau latéral À GAUCHE */}
          <div style={panelStyle}>
            <div style={panelHeaderStyle}>
              <h3 style={modalTitleStyle}>Options du rapport</h3>
              <button 
                style={closeButtonStyle} 
                onClick={() => setShowConfig(false)}
                title="Fermer"
              >
                ×
              </button>
            </div>
            
            <div style={panelContentStyle}>
              <div style={optionSectionStyle}>
                <label style={labelStyle}>
                  <input
                    type="checkbox"
                    checked={config.extraitTF}
                    onChange={e => setConfig({...config, extraitTF: e.target.checked})}
                  /> 
                  Inclure l'extrait du titre foncier
                </label>
                
                <label style={labelStyle}>
                  <input
                    type="checkbox"
                    checked={config.extraitPA}
                    onChange={e => setConfig({...config, extraitPA: e.target.checked})}
                  /> 
                  Ajouter l'extrait du PA
                </label>
              </div>
              
              {config.extraitPA && (
                <div style={paSectionStyle}>
                  <div style={uploadSectionStyle}>
                    <p style={sectionTitleStyle}>1. Télécharger l'extrait de PA</p>
                    <input 
                      type="file" 
                      accept="image/*,.pdf" 
                      onChange={handlePAUpload}
                      style={fileInputStyle}
                    />
                  </div>
                  
                  {showCropper && paSrc && (
                    <div style={cropSectionStyle}>
                      <p style={sectionTitleStyle}>2. Recadrez la zone du PA :</p>
                      <div style={{ position: 'relative', height: 300, border: '1px solid #ddd', borderRadius: '4px' }}>
                        <Cropper
                          image={paSrc}
                          crop={crop}
                          zoom={zoom}
                          aspect={4/3}
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onCropComplete={(_, pixels) => setCropPixels(pixels)}
                        />
                      </div>
                      <button
                        style={{...buttonStyle, marginTop: '10px', padding: '8px'}}
                        onClick={handleValidateCrop}
                      >
                        Valider le recadrage
                      </button>
                    </div>
                  )}
                  
                  {croppedPA && !showCropper && (
                    <div style={previewSectionStyle}>
                      <p style={sectionTitleStyle}>3. Aperçu de l'extrait de PA :</p>
                      <div style={previewContainerStyle}>
                        <img 
                          src={croppedPA} 
                          alt="Aperçu de l'extrait de PA" 
                          style={previewImageStyle}
                        />
                       
                      </div>
                     
                    </div>
                  )}
                </div>
              )}
              
              <button 
                style={{...buttonStyle, marginTop: '27px'}} 
                onClick={handleExportPDF} 
                disabled={isExporting || (config.extraitPA && !croppedPA)}
              >
                {isExporting ? 'Génération en cours...' : 'Générer le PDF'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};


const buttonStyle = {
  padding: "10px 20px",
  background: "linear-gradient(135deg, #3498db, #2980b9)",
  color: "white",
  border: "none",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: "600",
  cursor: "pointer",
  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  marginTop: "6px"
};

// Overlay semi-transparent
const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  zIndex: 9998,
};

const panelStyle = {
  position: "fixed",
  top: 0,
  left: 0,  
  width: "450px",
  maxWidth: "90vw",
  height: "90vh",
  background: "#fff",
  boxShadow: "5px 0 15px rgba(0,0,0,0.2)", 
  zIndex: 9999,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// En-tête du panneau
const panelHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "20px 25px",
  borderBottom: "1px solid #eee",
  backgroundColor: "#f8f9fa",
};

// Contenu du panneau (scrollable)
const panelContentStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "25px",
};

// Bouton de fermeture
const closeButtonStyle = {
  background: "none",
  border: "none",
  fontSize: "28px",
  cursor: "pointer",
  color: "#666",
  padding: "0",
  width: "30px",
  height: "30px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  transition: "all 0.2s",
};

// Autres styles
const modalTitleStyle = {
  margin: 0,
  color: "#2c3e50",
  fontSize: "18px",
};

const optionSectionStyle = {
  marginBottom: "10px",
  paddingBottom: "15px",
  borderBottom: "1px solid #eee"
};

const paSectionStyle = {
  marginTop: "8px",
  padding: "15px",
  backgroundColor: "#f8f9fa",
  borderRadius: "6px",
  border: "1px solid #e9ecef"
};

const uploadSectionStyle = {
  marginBottom: "10px"
};

const cropSectionStyle = {
  marginBottom: "10px"
};

const previewSectionStyle = {
  marginTop: "15px",
  paddingTop: "15px",
  borderTop: "1px dashed #ddd"
};

const sectionTitleStyle = {
  fontSize: "14px",
  fontWeight: "600",
  marginBottom: "5px",
  color: "#2c3e50"
};

const previewContainerStyle = {
  backgroundColor: "#fff",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ddd",
  marginBottom: "10px"
};

const previewImageStyle = {
  width: "100%",
  maxHeight: "200px",
  objectFit: "contain",
  borderRadius: "4px",
  marginBottom: "8px"
};

const previewActionsStyle = {
  display: "flex",
  gap: "10px",
  justifyContent: "center"
};

const actionButtonStyle = {
  padding: "6px 12px",
  color: "white",
  border: "none",
  borderRadius: "4px",
  fontSize: "12px",
  fontWeight: "500",
  cursor: "pointer",
  flex: 1
};

const previewInfoStyle = {
  fontSize: "12px",
  color: "#7f8c8d",
  fontStyle: "italic",
  marginTop: "5px",
  textAlign: "center"
};

const labelStyle = {
  display: "block",
  marginBottom: "12px",
  fontSize: "14px",
  cursor: "pointer",
  color: "#34495e"
};

const fileInputStyle = {
  marginTop: "5px",
  fontSize: "14px",
  width: "100%",
  padding: "8px",
  border: "1px solid #ddd",
  borderRadius: "4px",
  backgroundColor: "#fff"
};

export default ExportPDFButton;